import PropTypes from 'prop-types';
import { Component, Children, cloneElement } from 'react';
import styles from './Binder.scss';
import Sidebar from 'binder/components/Sidebar/Sidebar';
import lodash from 'lodash';
import { connect } from 'react-redux';
import BinderNavigation from 'binder/components/BinderNavigation/BinderNavigation';

import {
    binderValidationFeatureAccess,
    doaFeatureAccess,
    binderEventFeatureAccess,
} from 'common/features/predicates';

import {
    binderSetupTabPredicate,
    defaultBinderSetupPath,
} from 'common/permissions/predicates';

import { binderRouteSelector, homeRouteSelector } from 'common/features/selectors';

import { manageTasksSelector } from 'common/permissions/selectors';

const SetupPathnameRegexp = /\/users|externalUsers|setup$/;

function getPermittedTabs(userPermission, binderPermissions, binderId) {
    let permittedTabs = [
        {
            label: 'Documents',
            path: `/binder/${binderId}/files`
        }
    ];

    if (userPermission) {
        permittedTabs.push({
            label: 'Tasks',
            path: `/binder/${binderId}/tasks/`
        });
    }


    if (binderSetupTabPredicate({binderPermissions})) {
        permittedTabs.push({
            label: 'Setup',
            path: defaultBinderSetupPath({binderPermissions, binderId})
        });
    }

    return permittedTabs;
}

class Binder extends Component {
    constructor() {
        super(...arguments);
        this.lastPathname = '';
        this.contentStyle = '';
        this.setSidebarLinks = this.setSidebarLinks.bind(this);
        this.setSidebarIcons = this.setSidebarIcons.bind(this);
        this.setSidebarCollapsed = this.setSidebarCollapsed.bind(this);
        this.calculateFixedNav = this.calculateFixedNav.bind(this);
        const { binderId, manageTasksPermission, binderPermissions } = this.props;
        let tabs = getPermittedTabs(manageTasksPermission, binderPermissions, binderId);
        this.state = {
            tabs: tabs,
            links: [],
            iconClass: '',
            isCollapsed: false,
            fixedNav : false,
        };
    }

    isSetupPage() {
        const nextPathname = this.props.location.pathname;
        const isSetupDetailsRoute = nextPathname === `/binder/${this.props.binderId}`;
        const isSetupPage = isSetupDetailsRoute || SetupPathnameRegexp.test(nextPathname);

        return this.lastPathname !== nextPathname && isSetupPage;
    }

    displayTab(tabName, arrayMethod) {
        const updatedTabs = this.state.tabs;
        updatedTabs[arrayMethod]({
            label: tabName,
            path: `/binder/${this.props.binderId}/${tabName.toLowerCase()}`,
        });
        this.setState({
            tabs: updatedTabs,
        });
    }

    componentDidMount() {
        const { binder, binderValidationAccess, binderEventsAccess, doaAccess } = this.props;
        const hasBinderValidationTemplate = binder.hasBinderValidationTemplateEnabled;
        if (this.isSetupPage()) {
            this.setSetupSidebarLinks();
        }
        if (doaAccess) {
            this.displayTab('Delegation', 'push');
        }
        if (hasBinderValidationTemplate && binderValidationAccess) {
            this.displayTab('Validation', 'unshift');
        }
        if (binderEventsAccess) {
            this.displayTab('Events', 'push');
        }

        window.addEventListener('scroll', this.calculateFixedNav);
    }

    componentWillUnmount() {
        window.removeEventListener('scroll', this.calculateFixedNav);
    }

    calculateFixedNav() {
        const { fixedNav } = this.state;
        const TOP_NAVIGATION_HEIGHT_REM = 3.5;
        const currentPageOffsetRem = window.pageYOffset / 16;
        const hasScrolledPastTopNav = currentPageOffsetRem > TOP_NAVIGATION_HEIGHT_REM;

        if (hasScrolledPastTopNav && !fixedNav) {
            return this.setFixedNav(true);
        }

        if (!hasScrolledPastTopNav && fixedNav) {
            return this.setFixedNav(false);
        }

    }

    setFixedNav(isFixed) {
        this.setState({
            fixedNav: isFixed
        });
    }

    removeTab(tabLabel) {
        const updatedTabs = [...this.state.tabs];
        const index = updatedTabs.findIndex(t => t.label === tabLabel);
        if (index !== -1) {
            updatedTabs.splice(index, 1);
            this.setState({
                tabs: updatedTabs,
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if(prevProps.binderValidationAccess && !this.props.binderValidationAccess) {
            this.removeTab('Validation');
        }
        if (prevProps.doaAccess && !this.props.doaAccess) { 
            this.removeTab('Delegation');
        }
        if (prevProps.studyEventsAccess && !this.props.studyEventsAccess) {
            this.removeTab('Events');
        }

        if (this.isSetupPage()) {
            this.setSetupSidebarLinks();
        }

        if (!lodash.isEqual(prevProps.binder.eventsTabEnabled, this.props.binder.eventsTabEnabled)) {
            this.props.binder.eventsTabEnabled && this.displayTab('Events', 'push');
        }

        this.lastPathname = this.props.location.pathname;
        if (!lodash.isEqual(prevProps.location.pathname, this.lastPathname)) {
            // BUGFIX: Changing tabs will not reset the window scroll position,
            // leaving users in a place they may not expect.

            window.scrollTo(0, 0);
        }
    }

    setSetupSidebarLinks() {
        const { binderId } = this.props;
        this.setSidebarLinks([{
            label: 'Details',
            action: `/binder/${binderId}`,
            kind: 'details'
        }, {
            label: 'Internal Users',
            action: `/binder/${binderId}/users`,
            kind: 'internalUsers'
        }, {
            label: 'External Users',
            action: `/binder/${binderId}/externalUsers`,
            kind: 'externalUsers'
        }, {
            label: 'Setup',
            action: `/binder/${binderId}/setup`,
            kind: 'editBinderDetails',
        }]);
    }

    setSidebarLinks(links) {
        if (!Array.isArray(links)) {
            return;
        }

        this.setState({
            links
        });
    }

    setSidebarIcons(iconClass) {
        this.setState({
            iconClass
        });
    }

    setSidebarCollapsed(isCollapsed) {
        this.setState({
            isCollapsed
        });
    }

    render() {
        const { tabs, links, iconClass, isCollapsed, fixedNav } = this.state;
        const { binder, loadBinder, binderId, router, location, homeRoute, isStudy } = this.props;
        const { title } = binder;
        const childElement = cloneElement(Children.only(this.props.children), {
            binderId,
            setSidebarLinks: this.setSidebarLinks,
            setSidebarIcons: this.setSidebarIcons,
            setSidebarCollapsed: this.setSidebarCollapsed,
            reloadBinder: loadBinder
        });
        const sidebarProps = {
            links,
            heading: 'You have selected binder',
            title,
            iconClass,
            isCollapsed,
            fixedNav,
        };

        if (fixedNav) {
            if (isCollapsed) {
                this.contentStyle = styles.stuckContentFullWidth;
            } else {
                this.contentStyle = styles.stuckContent;
            }
        } else {
            if (isCollapsed) {
                this.contentStyle = styles.contentFullWidth;
            } else {
                this.contentStyle = styles.content;
            }
        }
        return (
            <div className={styles.binderContainer}  data-cy="binder">
                <BinderNavigation title={title} tabs={tabs} router={router} location={location} fixedNav={fixedNav} homeRoute={homeRoute} isStudy={isStudy} binderId={binderId} />
                <div className={styles.binder}>
                    <Sidebar {...sidebarProps} />
                    <div className={this.contentStyle} >
                        {childElement}
                    </div>
                </div>
            </div>
        );
    }
}

Binder.contextTypes = {
    location: PropTypes.any
};

Binder.propTypes = {
    children: PropTypes.element.isRequired,
    loadBinder: PropTypes.func.isRequired
};


export default connect((state, props) => {
    const manageTasksPermission = manageTasksSelector(state);
    const binderPermissions = lodash.get(state, 'me.binderPermissions', {});
    const binderValidationAccess = binderValidationFeatureAccess(state);
    const doaAccess = doaFeatureAccess(state);
    const binderEventsAccess = binderEventFeatureAccess(state, '', props.binderId);
    const isStudy = !!lodash.get(state, 'routing.locationBeforeTransitions.query.studyId');
    const homeRoute = isStudy ? homeRouteSelector(state) : binderRouteSelector(state);

    return {
        manageTasksPermission,
        binderPermissions,
        manageTasksSelector,
        binderValidationAccess,
        doaAccess,
        binderEventsAccess,
        homeRoute,
        isStudy,
    };
})(Binder);
